// Portal kabuk store'u (K8, Finar 2026-10-23) — müşterinin adına çalışabildiği firmalar, aktif
// firma ve onun dar markası ({clientName, logoUrl}). Seçim kullanıcı bazında
// `localStorage["portal.firm.<userId>"]` (logout'ta `clear-user-local-state` süpürür; her okuma/
// yazma try/catch — private mode'da seçim hatırlanmaz, akış kesilmez).
//
// Firma değişimi (`setActiveClient`) portal KPI store'unu SIFIRLAR — ClientShell `activeClientId`
// değişince set listesini aktif firmaya süzülmüş olarak yeniden yükler; ClientHome form listesini
// yeniden çeker ve aktif firmaya süzer. Firma listesi alınamazsa (`firmsStatus:"error"`) portal
// eski tek-firma davranışıyla çalışır (fail-open; süzme yok, başlıkta firma yok).

import { create } from "zustand";
import type { PortalBranding, PortalFirm } from "@mahir/shared";
import { findProjectClientId, getPortalBranding, listPortalFirms } from "../api/portal";
import { usePortalKpiStore } from "./portal-kpi-store";

export type PortalFirmsStatus = "idle" | "loading" | "ready" | "error";

export interface PortalState {
  userId: string | null;
  firmsStatus: PortalFirmsStatus;
  firms: PortalFirm[];
  activeClientId: string | null;
  branding: PortalBranding | null;

  /** Firma listesi + varsayılan seçim. `hintProjectId`: rota projesinin firması tercih edilir. */
  load: (userId: string, opts?: { hintProjectId?: string | null }) => Promise<void>;
  setActiveClient: (clientId: string) => Promise<void>;
  /**
   * Uygulama içi gezinme (TODO-KPI-57/10): rota bir form projesine aitse aktif firma o projenin firması
   * olur. Çok firmalıda çalışır; aynı firmaya ya da bilinmeyen/erişimsiz projeye dokunmaz.
   */
  followProject: (projectId: string) => Promise<void>;
  reset: () => void;
}

export function portalFirmKey(userId: string): string {
  return `portal.firm.${userId}`;
}

export function readPortalFirm(userId: string): string | null {
  try {
    return window.localStorage.getItem(portalFirmKey(userId));
  } catch {
    return null;
  }
}

function writePortalFirm(userId: string, clientId: string): void {
  try {
    window.localStorage.setItem(portalFirmKey(userId), clientId);
  } catch {
    // quota / private mode — seçim hatırlanmaz
  }
}

export const usePortalStore = create<PortalState>()((set, get) => {
  async function loadBranding(clientId: string): Promise<void> {
    try {
      const branding = await getPortalBranding(clientId);
      if (get().activeClientId === clientId) set({ branding });
    } catch {
      // marka alınamadı — başlıkta firma listesindeki ad gösterilir (logo yok)
    }
  }

  return {
    userId: null,
    firmsStatus: "idle",
    firms: [],
    activeClientId: null,
    branding: null,

    async load(userId, opts = {}) {
      const st = get();
      if (st.userId === userId && (st.firmsStatus === "ready" || st.firmsStatus === "loading"))
        return;
      // Final review I4: aynı sekmede başka kullanıcı → önceki firma/marka/KPI hiç render edilmez.
      if (st.userId !== userId) {
        if (st.userId !== null) usePortalKpiStore.getState().reset();
        set({ firms: [], activeClientId: null, branding: null });
      }
      set({ userId, firmsStatus: "loading" });
      try {
        const firms = await listPortalFirms();
        if (get().userId !== userId) return; // bu arada çıkış/başka kullanıcı
        const has = (id: string | null): id is string =>
          id !== null && firms.some((f) => f.clientId === id);
        let active: string | null = null;
        if (firms.length > 1 && opts.hintProjectId) {
          const fromRoute = await findProjectClientId(opts.hintProjectId).catch(() => null);
          if (has(fromRoute)) active = fromRoute;
        }
        if (!active) {
          const stored = readPortalFirm(userId);
          active = has(stored) ? stored : (firms[0]?.clientId ?? null);
        }
        set({ firms, firmsStatus: "ready", activeClientId: active, branding: null });
        if (active) {
          writePortalFirm(userId, active);
          await loadBranding(active);
        }
      } catch {
        if (get().userId !== userId) return;
        set({ firms: [], firmsStatus: "error", activeClientId: null, branding: null });
      }
    },

    async setActiveClient(clientId) {
      const { userId, activeClientId, firms } = get();
      if (clientId === activeClientId || !firms.some((f) => f.clientId === clientId)) return;
      // Önceki firmanın KPI seti/ızgarası yeni firmada görünmesin (ClientShell yeniden yükler).
      usePortalKpiStore.getState().reset();
      set({ activeClientId: clientId, branding: null });
      if (userId) writePortalFirm(userId, clientId);
      await loadBranding(clientId);
    },

    async followProject(projectId) {
      const { firms, userId } = get();
      if (firms.length <= 1) return;
      const clientId = await findProjectClientId(projectId).catch(() => null);
      if (get().userId !== userId) return; // bu arada çıkış/başka kullanıcı
      if (!clientId || clientId === get().activeClientId) return;
      if (!get().firms.some((f) => f.clientId === clientId)) return;
      await get().setActiveClient(clientId);
    },

    reset() {
      set({ userId: null, firmsStatus: "idle", firms: [], activeClientId: null, branding: null });
    },
  };
});

/** Aktif firmanın başlık adı: marka yanıtı → firma listesi → null. */
export function activeFirmName(
  s: Pick<PortalState, "branding" | "firms" | "activeClientId">,
): string | null {
  if (s.branding) return s.branding.clientName;
  return s.firms.find((f) => f.clientId === s.activeClientId)?.clientName ?? null;
}
